import React, { FC, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Page, Text, View, Document, StyleSheet, PDFViewer, pdf, Line, Svg, Image } from '@react-pdf/renderer';

import { useAppSelector } from '~redux/hooks';
import { GoldTypeEnum, TransactionActionEnum, TransactionStatusEnum } from '~utils/enums';

import AffinLogo from '~assets/images/affin-logo-v1.png';
import * as SummaryStyles from './_receiptPDF.module.scss';
import { TTransactionListing, TTransactionListingItems } from '~types/feat/gold';
import ActionButton from '~components/atoms/ActionButton';
import Decimal from 'decimal.js';

type PageProps = {
    children: React.ReactNode;
    title: string;
    subtitle: string;
    copy: string;
    isCustomerCopy?: boolean;
}

type ReceiptPDFProps = {
    transactionDetails: TTransactionListing;
}

const ReceiptPDF: FC<ReceiptPDFProps> = ({
    transactionDetails,
}) => {
    const [loading, setLoading] = useState(false);
    const [newSummaryTable, setNewSummaryTable] = useState<Array<{ label: string; value: string | number; }>>([]);

    const getTotalGoldWeight = (item: TTransactionListingItems) => {
        if (transactionDetails.goldType === GoldTypeEnum.PhysicalGold){
            if (item.physicalGoldWeight) return (((Number(item.physicalGoldWeight) * item.amount) * 1000) / 1000).toFixed(3);

            return '-';
        }

        return ((item.amount * 1000) / 1000).toFixed(3);
    };

    const renderSerialNo = (item: TTransactionListingItems) => {
        if (transactionDetails.goldType === GoldTypeEnum.PaperGold) return ' N/A';
        if (!item.serialNo) return ' N/A';

        return item.serialNo.map(code => code.toUpperCase()).join(', ');
    };

    const itemsSummary = transactionDetails.items.flatMap((item, index) => ([
        { label: `Item ${index + 1}`, value: '' },
        { label: 'Product Name / Nama Produk', value: item.productName },
        { label: 'Quantity / Kuantiti (Gram)', value: getTotalGoldWeight(item) },
        { label: 'Serial No.', value: renderSerialNo(item) },
        { label: 'Discount Name', value: item.discountName ?? '' },
        { label: 'Discount (MYR/%)', value: item.discountedPrice ? `RM${item.discountedPrice}` : '' },
    ]));

    const summaryTable = [
        {
            label: 'Transaction Status / Status Transaksi',
            value: TransactionStatusEnum[transactionDetails.status],
        },
        {
            label: 'Transaction Date/Time / Tarikh/Masa Transaksi',
            value: dayjs(transactionDetails.createdAt).format('DD/MM/YYYY hh:mm A'),
        },
        {
            label: 'Recipient Reference No. / No. Rujukan Penerima',
            value: transactionDetails.transactionId,
        },
        {
            label: 'Branch Name / Nama Cawangan',
            value: transactionDetails.branchName,
        },
        {
            label: 'Name of Customer/Company/Firm/Association / Nama Pelanggan/Syarikat/Firma/Persatuan',
            value: transactionDetails.name || '',
        },
        {
            label: 'Customer ID / ID Pelanggan',
            value: transactionDetails.idNo || '',
        },
        {
            label: 'Type of Account / Jenis Akaun',
            value: '',
        },
        {
            label: 'Debit Account / Akaun Debit',
            value: '',
        },
        {
            label: 'Credit Account / Akaun Kredit',
            value: '',
        },
        {
            label: transactionDetails.action === TransactionActionEnum['Bank Sell'] ? 'Deposit Form / Bentuk Deposit' : 'Redemptions Form / Bentuk Penebusan',
            value: '', // TODO: how to check if the customer buy using value or gram?
        },
        ...itemsSummary,
        {
            label: 'Total Amount / Jumlah Amaun (RM)',
            value: `RM${new Decimal(transactionDetails.transactionTotalNetValue || 0).toFixed(2)}`,
        },
    ];

    useEffect(() => {
        if (transactionDetails.child !== null) {
            setNewSummaryTable(
                [
                    {
                        label: 'Transaction Status / Status Transaksi',
                        value: TransactionStatusEnum[transactionDetails.status],
                    },
                    {
                        label: 'Transaction Date/Time / Tarikh/Masa Transaksi',
                        value: dayjs(transactionDetails.createdAt).format('DD/MM/YYYY hh:mm A'),
                    },
                    {
                        label: 'Recipient Reference No. / No. Rujukan Penerima',
                        value: transactionDetails.transactionId,
                    },
                    {
                        label: 'Branch Name / Nama Cawangan',
                        value: transactionDetails.branchName,
                    },
                    {
                        label: 'Name of Customer/Company/Firm/Association / Nama Pelanggan/Syarikat/Firma/Persatuan',
                        value: transactionDetails.name || '',
                    },
                    {
                        label: 'Customer ID / ID Pelanggan',
                        value: transactionDetails.idNo || '',
                    },
                    {
                        label: 'Child\'s Name / Nama Anak',
                        value: transactionDetails.child.childName,
                    },
                    {
                        label: 'Type of Account / Jenis Akaun',
                        value: '',
                    },
                    {
                        label: 'Debit Account / Akaun Debit',
                        value: '',
                    },
                    {
                        label: 'Credit Account / Akaun Kredit',
                        value: '',
                    },
                    {
                        label: transactionDetails.action === TransactionActionEnum['Bank Sell'] ? 'Deposit Form / Bentuk Deposit' : 'Redemptions Form / Bentuk Penebusan',
                        value: '', // TODO: how to check if the customer buy using value or gram?
                    },
                    ...itemsSummary,
                    {
                        label: 'Total Amount / Jumlah Amaun (RM)',
                        value: Number(transactionDetails.items[0].totalNetValue).toLocaleString('en-US', { style: 'decimal', minimumFractionDigits: 2 }),
                    },
                ]
            );
        }
        else {
            setNewSummaryTable(summaryTable);
        }
    }, [transactionDetails]);

    const goldTable = transactionDetails.items.map(item => {
        return {
            productName: item.productName,
            pricePerGram: (Math.round(Number(item.goldPrice) * 100) / 100).toFixed(2),
        };
    });

    // const goldTable = {
    //     productName: transactionDetails.productName,
    //     pricePerGram: typeof transactionDetails.goldPrice === 'number' ? (Math.round(transactionDetails.goldPrice * 100) / 100).toFixed(2) : '-',
    // };

    const customerDetails = {
        name: transactionDetails.name,
        idNo: transactionDetails.idNo,
    };

    const PageWithHeaderFooter = ({
        children,
        title,
        subtitle,
        copy,
        isCustomerCopy,
    }: PageProps) => (
        <Page size='A4' style={isCustomerCopy ? styles.customerPage : styles.page} orientation='landscape'>
            {/* Header */}
            <View style={styles.header}>
                {
                    isCustomerCopy &&
                    <Image
                        style={styles.headerLogo}
                        src={AffinLogo}
                    />
                }
                <View style={styles.headerContent}>
                    <Text style={styles.headerTitle}>{title}</Text>
                    <Text style={styles.headerSubtitle}>{subtitle}</Text>
                </View>
                <Text style={styles.headerCopy}>{copy}</Text>
                <View style={styles.divider} />
            </View>

            {/* Main Content */}
            <View style={styles.mainContent}>
                {children}
            </View>
        </Page>
    );

    const renderDetails = () => {
        return (
            <View style={styles.detailsTable}>
                {newSummaryTable.map((item, index) => (
                    <View style={styles.detailsRow} key={index}>
                        <Text style={styles.detailsCell}>{item.label}</Text>
                        <Text style={styles.detailsCell}>{item.label.includes('Item') ? '' : `: ${item.value}`}</Text>
                    </View>
                ))}
            </View>
        );
    };

    const renderGoldDescription = () => {
        return (
            <View>
                <Text style={styles.goldDescriptionTitle}>Description / Deskripsi</Text>
                <View style={styles.goldTable}>
                    <View style={styles.goldTableRow}>
                        <Text style={styles.goldHeaderCell}>Product / Produk</Text>
                        <Text style={styles.goldHeaderCellPrice}>Price Per Gram / Harga Segram</Text>
                    </View>
                    {
                        goldTable.map((gold, index) => (
                            <View key={index} style={styles.goldTableRow}>
                                <Text style={[styles.goldTableCell, { width: '70%' }]}>{gold.productName}</Text>
                                <Text style={[styles.goldTableCell, { width: '30%' }]}>{gold.pricePerGram}</Text>
                            </View>
                        ))
                    }
                </View>
            </View>
        );
    };

    const renderDeclaration = () => {

        if (transactionDetails.action === TransactionActionEnum['Bank Buy']) {
            return (
                <View style={styles.declarationContainer}>
                    <Text style={styles.declarationText}>I/We hereby confirm that the physical gold denomination(s) stated above has/have been surrendered to the Bank.</Text>
                    <Text style={styles.declarationText}>Saya/Kami dengan ini mengesahkan bahawa denominasi emas fizikal yang dinyatakan di atas telah diserahkan kepada pihak Bank.</Text>
                </View>
            );
        }

        return (
            <View>
                <View style={styles.declarationContainer}>
                    <Text style={styles.declarationText}>I/We hereby acknowledge and confirm that I/we have received the physical gold as per Gold Denomination and transaction stated above.</Text>
                    <Text style={styles.declarationText}>Saya/Kami dengan ini mengakui dan mengesahkan bahawa saya/kami telah menerima emas fizikal seperti Denominasi Emas dan transaksi yang dinyatakan di atas.</Text>
                </View>

                <View style={styles.declarationContainer}>
                    <Text style={styles.declarationText}>I/We further declare that I/We entitled to the right of the said gold Serial Number(s) as stipulated in the Receipt above, unless I/We have transferred or assigned the particular Serial Number(s) to other party.</Text>
                    <Text style={styles.declarationText}>The Bank shall have no obligation to check on the ownership status of the gold surrendered to be resold to the Bank.</Text>
                    <Text style={styles.declarationText}>Saya/Kami mengisytiharkan bahawa saya/kami berhak kepada hak Nombor Siri emas seperti yang dinyatakan di dalam Resit di atas, melainkan saya/kami telah memindahkan atau menyerahkan Nombor Siri tertentu </Text>
                    <Text style={styles.declarationText}>kepada pihak lain. Pihak Bank tidak mempunyai kewajipan untuk menyemak status pemilikan emas yang diserahkan untuk dijual semula kepada pihak Bank.</Text>
                </View>

                <View style={styles.declarationContainer}>
                    <Text style={styles.declarationText}>I / We hereby agree to indemnify the Bank at all times and keep the Bank indemnified against any and all claims, damages, losses, costs, liabilities, expenses and all other sums of money, which may be against or </Text>
                    <Text style={styles.declarationText}>incurred by the Bank as a result of the transfer and assign of the particular Serial Number(s) to anyone or any consequence that may arise from my /our action such losses, damages or expenses are attributable to the default, </Text>
                    <Text style={styles.declarationText}>negligence or fraud of the Bank.</Text>
                    <Text style={styles.declarationText}>Saya / Kami dengan ini bersetuju untuk melepaskan pihak Bank pada setiap masa dan menanggung rugi pihak Bank terhadap sebarang dan semua tuntutan, ganti rugi, kerugian, kos, liabiliti, perbelanjaan dan semua </Text>
                    <Text style={styles.declarationText}>wang lain yang mungkin bertentangan atau ditanggung oleh pihak Bank akibat pemindahan atau penyerahan Nombor Siri tertentu kepada sesiapa atau sebarang akibat yang mungkin timbul daripada tindakan saya / kami</Text>
                    <Text style={styles.declarationTextBold}>kecuali kehilangan, ganti rugi atau perbelanjaan tersebut disebabkan oleh kemungkiran, kecuaian atau penipuan oleh pihak Bank.</Text>
                </View>

                <View style={styles.declarationContainer}>
                    <Text style={styles.declarationText}>I Hereby confirm that the below information was disclosed to me:</Text>
                    <Text style={styles.declarationText}>Dengan ini saya mengesahkan bahawa maklumat di bawah telah dinyatakan kepada saya:</Text>
                </View>

                <View style={styles.declarationContainer}>
                    <Text style={styles.declarationText}>(a) Non-DTM deposit products i.e. Savings Account of Lembaga Tabung Haji & Savings Scheme of Skim Simpanan Pendidikan Nasional or Non-deposit products/ accounts i.e. Unit trust Schemes, Securities Trading is </Text>
                    <Text style={styles.declarationText}>not protected by Perbadanan Insurans Deposit Malaysia (PIDM).</Text>
                    <Text style={styles.declarationText}>Produk deposit anggota bukan penerima deposit (bukan DTM) iaitu Akaun Simpanan Lembaga Tabung Haji & Skim Simpanan Pendidikan Nasional atau produk/akaun bukan deposit iaitu Skim Unit Amanah, Perdagangan Sekuriti </Text>
                    <Text style={styles.declarationText}>tidak dilindungi oleh Perbadanan Insurans Deposit Malaysia (PIDM).</Text>
                </View>

                <View style={styles.declarationContainer}>
                    <Text style={styles.declarationText}>(b) Any money withdrawn from a covered deposit for non-DTM deposit products i.e. Savings Account of Lembaga Tabung Haji & Savings Scheme of Skim Simpanan Pendidikan Nasional or Non-deposit products/ </Text>
                    <Text style={styles.declarationText}>accounts i.e. Unit trust Schemes, Securities trading is no longer protected by Perbadanan Insurans Deposit Malaysia (PIDM).</Text>
                    <Text style={styles.declarationText}>Apa-apa wang yang dikeluarkan dari deposit yang dilindungi bagi produk deposit anggota bukan penerima deposit (bukan DTM) iaitu Akaun Simpanan Lembaga Tabung Haji & Skim Simpanan Pendidikan Nasional atau </Text>
                    <Text style={styles.declarationText}>produk / akaun bukan deposit iaitu Skim Unit Amanah, perdagangan sekuriti tidak dilindungi oleh Perbadanan Insurans Deposit Malaysia (PIDM).</Text>
                </View>

                <View style={styles.declarationContainer}>
                    <Text style={styles.declarationText}>(c) Physical Gold offered and sold under AFFIN PRECIOUS METAL is not protected by Perbadanan Insurans Deposit Malaysia (PIDM).</Text>
                    <Text style={styles.declarationText}>Emas Fizikal yang ditawarkan/dijual di bawah AFFIN PRECIOUS METAL tidak dilindungi oleh Perbadanan Insurans Deposit Malaysia (PIDM).</Text>
                </View>
            </View>
        );
    };

    const renderDisclaimer = () => {
        if (transactionDetails.action === TransactionActionEnum['Bank Buy']) return;

        return (
            <View>
                <View style={styles.declarationContainer}>
                    <Text style={styles.declarationText}>I/We hereby acknowledge and confirm that I/we have received the physical gold as per Gold Denomination and transaction stated above.</Text>
                    <Text style={styles.declarationText}>Saya/Kami dengan ini mengakui dan mengesahkan bahawa saya/kami telah menerima emas fizikal seperti Denominasi Emas dan transaksi yang dinyatakan di atas.</Text>
                </View>

                <View style={styles.declarationContainer}>
                    <Text style={styles.declarationText}>I/We further declare that I/We entitled to the right of the said gold Serial Number(s) as stipulated in the Receipt above, unless I/We have transferred or assigned the particular Serial Number(s) to other party.</Text>
                    <Text style={styles.declarationText}>The Bank shall have no obligation to check on the ownership status of the gold surrendered to be resold to the Bank.</Text>
                    <Text style={styles.declarationText}>Saya/Kami mengisytiharkan bahawa saya/kami berhak kepada hak Nombor Siri emas seperti yang dinyatakan di dalam Resit di atas, melainkan saya/kami telah memindahkan atau menyerahkan Nombor Siri tertentu </Text>
                    <Text style={styles.declarationText}>kepada pihak lain. Pihak Bank tidak mempunyai kewajipan untuk menyemak status pemilikan emas yang diserahkan untuk dijual semula kepada pihak Bank.</Text>
                </View>

                <View style={styles.declarationContainer}>
                    <Text style={styles.declarationText}>I / We hereby agree to indemnify the Bank at all times and keep the Bank indemnified against any and all claims, damages, losses, costs, liabilities, expenses and all other sums of money, which may be against or </Text>
                    <Text style={styles.declarationText}>incurred by the Bank as a result of the transfer and assign of the particular Serial Number(s) to anyone or any consequence that may arise from my /our action such losses, damages or expenses are attributable to the default, </Text>
                    <Text style={styles.declarationText}>negligence or fraud of the Bank.</Text>
                    <Text style={styles.declarationText}>Saya / Kami dengan ini bersetuju untuk melepaskan pihak Bank pada setiap masa dan menanggung rugi pihak Bank terhadap sebarang dan semua tuntutan, ganti rugi, kerugian, kos, liabiliti, perbelanjaan dan semua </Text>
                    <Text style={styles.declarationText}>wang lain yang mungkin bertentangan atau ditanggung oleh pihak Bank akibat pemindahan atau penyerahan Nombor Siri tertentu kepada sesiapa atau sebarang akibat yang mungkin timbul daripada tindakan saya / kami </Text>
                    <Text style={styles.declarationTextBold}>kecuali kehilangan, ganti rugi atau perbelanjaan tersebut disebabkan oleh kemungkiran, kecuaian atau penipuan oleh pihak Bank.</Text>
                </View>

                <View style={styles.declarationContainer}>
                    <Text style={styles.declarationText}>I Hereby confirm that the below information was disclosed to me:</Text>
                    <Text style={styles.declarationText}>Dengan ini saya mengesahkan bahawa maklumat di bawah telah dinyatakan kepada saya:</Text>
                </View>

                <View style={styles.declarationContainer}>
                    <Text style={styles.declarationText}>(a) Non-DTM deposit products i.e. Savings Account of Lembaga Tabung Haji & Savings Scheme of Skim Simpanan Pendidikan Nasional or Non-deposit products/ accounts i.e. Unit trust Schemes, Securities Trading is </Text>
                    <Text style={styles.declarationText}>not protected by Perbadanan Insurans Deposit Malaysia (PIDM).</Text>
                    <Text style={styles.declarationText}>Produk deposit anggota bukan penerima deposit (bukan DTM) iaitu Akaun Simpanan Lembaga Tabung Haji & Skim Simpanan Pendidikan Nasional atau produk/akaun bukan deposit iaitu Skim Unit Amanah, Perdagangan Sekuriti </Text>
                    <Text style={styles.declarationText}>tidak dilindungi oleh Perbadanan Insurans Deposit Malaysia (PIDM).</Text>
                </View>

                <View style={styles.declarationContainer}>
                    <Text style={styles.declarationText}>(b) Any money withdrawn from a covered deposit for non-DTM deposit products i.e. Savings Account of Lembaga Tabung Haji & Savings Scheme of Skim Simpanan Pendidikan Nasional or Non-deposit products/ </Text>
                    <Text style={styles.declarationText}>accounts i.e. Unit trust Schemes, Securities trading is no longer protected by Perbadanan Insurans Deposit Malaysia (PIDM).</Text>
                    <Text style={styles.declarationText}>Apa-apa wang yang dikeluarkan dari deposit yang dilindungi bagi produk deposit anggota bukan penerima deposit (bukan DTM) iaitu Akaun Simpanan Lembaga Tabung Haji & Skim Simpanan Pendidikan Nasional atau </Text>
                    <Text style={styles.declarationText}>produk / akaun bukan deposit iaitu Skim Unit Amanah, perdagangan sekuriti tidak dilindungi oleh Perbadanan Insurans Deposit Malaysia (PIDM).</Text>
                </View>

                <View style={styles.declarationContainer}>
                    <Text style={styles.declarationText}>(c) Physical Gold offered and sold under AFFIN PRECIOUS METAL is not protected by Perbadanan Insurans Deposit Malaysia (PIDM).</Text>
                    <Text style={styles.declarationText}>Emas Fizikal yang ditawarkan/dijual di bawah AFFIN PRECIOUS METAL tidak dilindungi oleh Perbadanan Insurans Deposit Malaysia (PIDM).</Text>
                </View>
            </View>
        );
    };

    const renderSignature = () => {
        return (
            <View style={styles.signatureContainer}>
                <View style={styles.signatureRow}>
                    <View style={styles.signatureCell}>
                        <Text>Customer&apos;s Signature / Tandatangan Pelanggan</Text>
                    </View>
                    <View style={styles.signatureCell}>
                        <Text>:</Text>
                        <Svg height='1' width='500'>
                            <Line
                                x1='5'
                                y1='0'
                                x2='200'
                                y2='0'
                                strokeWidth={2}
                                stroke='rgb(0, 0, 0)'
                            />
                        </Svg>
                    </View>
                </View>
                <View style={styles.signatureRow}>
                    <View style={styles.signatureCell}>
                        <Text>Customer/Authorised Signatory Name / Nama Pelanggan/Penandatangan Sah</Text>
                    </View>
                    <View style={styles.signatureCell}>
                        <Text>{`: ${customerDetails.name}`}</Text>
                    </View>
                </View>
                <View style={styles.signatureRow}>
                    <View style={styles.signatureCell}>
                        <Text>NRIC No. / No. Kad Pengenalan</Text>
                    </View>
                    <View style={styles.signatureCell}>
                        <Text>{`: ${customerDetails.idNo}`}</Text>
                    </View>
                </View>
                <View style={styles.signatureRow}>
                    <View style={styles.signatureCell}>
                        <Text>Date / Tarikh</Text>
                    </View>
                    <View style={styles.signatureCell}>
                        <Text>:</Text>
                    </View>
                </View>
                <View style={styles.signatureRow}>
                    <View style={styles.signatureCell}>
                        <Text>Time / Masa</Text>
                    </View>
                    <View style={styles.signatureCell}>
                        <Text>:</Text>
                    </View>
                </View>
            </View>
        );
    };

    const renderExtraSignature = () => {
        return (
            <View style={styles.extraSignature}>
                <View style={styles.extraSignatureRow}>
                    <View style={[styles.extraSignatureCell, { width: '60%' }]}>
                        <Text style={{ fontFamily: 'Helvetica-Bold' }}>(If applicable) / (Jika Berkenaan)</Text>
                    </View>
                </View>
                <View style={styles.extraSignatureRow}>
                    <View style={[styles.extraSignatureCell, { width: '60%' }]}>
                        <Text>Customer&apos;s Signature / Tandatangan Pelanggan</Text>
                    </View>
                    <View style={styles.extraSignatureCell}>
                        <Text>:</Text>
                        <Svg height='1' width='150'>
                            <Line
                                x1='5'
                                y1='0'
                                x2='150'
                                y2='0'
                                strokeWidth={2}
                                stroke='rgb(0, 0, 0)'
                            />
                        </Svg>
                    </View>
                </View>
                <View style={styles.extraSignatureRow}>
                    <View style={[styles.extraSignatureCell, { width: '60%' }]}>
                        <Text>Customer/Authorised Signatory Name / Nama Pelanggan/Penandatangan Sah</Text>
                    </View>
                    <View style={styles.extraSignatureCell}>
                        <Text>:</Text>
                    </View>
                </View>
                <View style={styles.extraSignatureRow}>
                    <View style={[styles.extraSignatureCell, { width: '60%' }]}>
                        <Text>NRIC No. / No. Kad Pengenalan</Text>
                    </View>
                    <View style={styles.extraSignatureCell}>
                        <Text>:</Text>
                    </View>
                </View>
                <View style={styles.extraSignatureRow}>
                    <View style={[styles.extraSignatureCell, { width: '60%' }]}>
                        <Text>Date / Tarikh</Text>
                    </View>
                    <View style={styles.extraSignatureCell}>
                        <Text>:</Text>
                    </View>
                </View>
                <View style={styles.extraSignatureRow}>
                    <View style={[styles.extraSignatureCell, { width: '60%' }]}>
                        <Text>Time / Masa</Text>
                    </View>
                    <View style={styles.extraSignatureCell}>
                        <Text>:</Text>
                    </View>
                </View>
            </View>
        );
    };

    const renderOfficeInputs = () => {
        return (
            <View style={styles.bankTable}>
                <View style={styles.bankTableRow}>
                    <Text style={styles.bankHeaderCell}>FOR BANK&apos;S USE ONLY / UNTUK KEGUNAAN BANK SAHAJA</Text>
                </View>
                <View style={styles.bankTableRow}>
                    <View style={styles.bankTableCell}>
                        <Text>
                            Accepted by (Teller) / Diterima oleh (Juruwang) :
                        </Text>
                        <Svg height='20' width='400'>
                            <Line
                                x1='0'
                                y1='20'
                                x2='200'
                                y2='20'
                                strokeWidth={2}
                                stroke='rgb(0, 0, 0)'
                            />
                        </Svg>
                    </View>
                    <View style={styles.bankTableCell}>
                        <Text>
                            Verified and Approved by (Officer/Branch Manager) / Disahkan dah Diluluskan oleh (Pegawai/Pengurus Bank) :
                        </Text>
                        <Svg height='20' width='400'>
                            <Line
                                x1='0'
                                y1='20'
                                x2='200'
                                y2='20'
                                strokeWidth={2}
                                stroke='rgb(0, 0, 0)'
                            />
                        </Svg>
                    </View>
                </View>
                <View style={styles.bankTableRow}>
                    <Text style={styles.bankTableCell}>{`Name / Nama : ${transactionDetails.branchTellerName}`}</Text>
                    <Text style={styles.bankTableCell}>Name / Nama :</Text>
                </View>
                <View style={styles.bankTableRow}>
                    <Text style={styles.bankTableCell}>Staff ID / ID Staf :</Text>
                    <Text style={styles.bankTableCell}>Staff ID / ID Staf :</Text>
                </View>
                <View style={styles.bankTableRow}>
                    <Text style={styles.bankTableCell}>Date / Tarikh :</Text>
                    <Text style={styles.bankTableCell}>Date / Tarikh :</Text>
                </View>
                <View style={styles.bankTableRow}>
                    <Text style={styles.bankTableCell}>Bank&apos;s Stamp / Cop Bank :</Text>
                    <Text style={styles.bankTableCell}>Bank&apos;s Stamp / Cop Bank :</Text>
                </View>
            </View>
        );
    };

    const renderReceiptFooter = () => {
        return (
            <View style={styles.receiptFooterContainer}>
                <Text style={styles.receiptFooterTitle}>Note: This receipt is computer generated and no signature is required.</Text>
                <Text style={styles.receiptFooterTitle}>Nota: Resit ini ialah cetakan komputer dan tiada tandatangan diperlukan.</Text>
                <View style={styles.divider} />
            </View>
        );
    };

    const handleOpenPDFInNewTab = () => {
        setLoading(true);

        const docTitle = transactionDetails.goldType === GoldTypeEnum.PaperGold
            ? transactionDetails.action === TransactionActionEnum['Bank Sell'] ? 'DEPOSIT OF AFFIN EMAS ACCOUNT-I / DEPOSIT AKAUN AFFIN EMAS-I' : 'REDEMPTIONS OF AFFIN EMAS ACCOUNT-I / REDEMPTIONS AKAUN AFFIN EMAS-I'
            : transactionDetails.action === TransactionActionEnum['Bank Sell'] ? 'DEPOSIT OF AFFIN EMAS-I / DEPOSIT AFFIN EMAS-I' : 'REDEMPTIONS OF AFFIN EMAS-I / REDEMPTIONS AFFIN EMAS-I';

        // Create the PDF document
        const doc = (
            <Document>
                <PageWithHeaderFooter
                    title={docTitle}
                    subtitle='RECEIPT / RESIT'
                    copy={'Customer\'s Copy'}
                    isCustomerCopy
                >
                    {renderDetails()}
                    {renderGoldDescription()}
                    {renderDisclaimer()}
                    {renderReceiptFooter()}
                </PageWithHeaderFooter>

                <PageWithHeaderFooter
                    title={docTitle}
                    subtitle='RECEIPT / RESIT'
                    copy={'Bank\'s Copy'}
                >
                    {renderDetails()}
                    {renderGoldDescription()}
                    {renderDeclaration()}
                    {renderSignature()}
                    <View style={styles.extraSignatureContainer}>
                        {renderExtraSignature()}
                        {renderExtraSignature()}
                    </View>
                    {renderOfficeInputs()}
                </PageWithHeaderFooter>
            </Document>
        );

        // Generate PDF Blob and open in new tab
        pdf(doc).toBlob().then(() => {
            const promise = pdf(doc).toBlob();
            promise.then((blob) => {
                const pdfUrl = URL.createObjectURL(blob);
                setLoading(false);
                window.open(pdfUrl, '_blank');
            }).catch(() => {
                setLoading(false);
            });
        });
    };
    return (
        <div>
            <ActionButton
                label='Print Receipt'
                onClick={handleOpenPDFInNewTab}
                isLoading={loading}
            />
        </div>
    );
};

// Create styles
const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        paddingTop: 50, // Space for header
        // paddingBottom: 80, // Space for footer
        paddingHorizontal: 20,
        position: 'relative',
    },
    customerPage: {
        flexDirection: 'column',
        paddingTop: 100, // Space for header
        // paddingBottom: 80, // Space for footer
        paddingHorizontal: 20,
        position: 'relative',
    },
    header: {
        position: 'absolute',
        top: 10,
        left: 20,
        right: 20,
        display: 'flex',
        flexDirection: 'column',
    },
    headerLogo: {
        width: 162,
        height: 50,
        padding: 5,
    },
    headerContent: {
        flexDirection: 'column',
        gap: 3,
        marginBottom: 5,
    },
    headerTitle: {
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
    },
    headerSubtitle: {
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
    },
    headerCopy: {
        position: 'absolute',
        top: 5,
        right: 20,
        fontSize: 6,
        color: '#666',
    },
    divider: {
        height: 2,
        backgroundColor: '#000',
        marginVertical: 3,
    },
    footer: {
        position: 'absolute',
        bottom: 20,
        left: 20,
        right: 20,
        display: 'flex',
        flexDirection: 'column',
    },
    footerContent: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 10,
    },
    pageNumber: {
        fontSize: 6,
        color: '#666',
    },
    mainContent: {
        flex: 1,
    },
    detailsTable: {
        display: 'flex',
        width: '100%',
        fontSize: 6,
        gap: 2,
        marginBottom: 15,
    },
    detailsRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    detailsCell: {
        width: '50%',
    },
    goldDescriptionTitle: {
        marginBottom: 5,
        fontSize: 6,
        fontFamily: 'Helvetica-Bold',
        textDecoration: 'underline',
    },
    goldTable: {
        width: '60%', // Full width
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#000000',
        borderBottomWidth: 0,
        borderRightWidth: 0,
        fontSize: 6,
        marginBottom: 15,
    },
    goldTableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderColor: '#000000',
    },
    goldHeaderCell: {
        width: '70%', // First column
        padding: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#000000',
        fontWeight: 'bold',
    },
    goldHeaderCellPrice: {
        width: '30%', // Second column
        padding: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#000000',
        fontWeight: 'bold',
    },
    goldTableCell: {
        padding: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#000000',
    },
    declarationContainer: {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: 6,
    },
    declarationTitle: {
        marginBottom: 5,
        fontSize: 6,
        fontFamily: 'Helvetica-Bold',
        textDecoration: 'underline',
    },
    declarationText: {
        fontSize: 6,
    },
    declarationTextBold: {
        fontSize: 6,
        fontFamily: 'Helvetica-Bold',
    },
    signatureContainer: {
        display: 'flex',
        width: '100%',
        fontSize: 6,
        gap: 1,
        marginTop: 25,
        marginBottom: 20,
    },
    signatureRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    signatureCell: {
        width: '50%',
    },
    extraSignatureContainer: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        fontSize: 6,
        gap: 5,
        marginBottom: 15,
    },
    extraSignature: {
        width: '50%',
    },
    extraSignatureRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    extraSignatureCell: {
        width: '40%',
        maxLines: 1,
    },
    bankTable: {
        width: '100%', // Full width
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#000000',
        borderBottomWidth: 0,
        borderRightWidth: 0,
        fontSize: 6,
        marginBottom: 15,
    },
    bankTableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderColor: '#000000',
    },
    bankHeaderCell: {
        width: '100%', // First column
        padding: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#000000',
        fontWeight: 'bold',
    },
    bankTableCell: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: 4,
        borderRightWidth: 1,
        borderColor: '#000000',
    },
    receiptFooterContainer: {
        flexDirection: 'column',
        marginBottom: 5,
        marginTop: 20,
    },
    receiptFooterTitle: {
        fontSize: 6,
    },
    receiptFooterSubtitle: {
        fontSize: 6,
    },
});

export default ReceiptPDF;
